import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, Info } from 'lucide-react';
import { addDays, formatYmd, localDateToYmd, ptYesterday, ymdToLocalDate } from '@/shared/ptDates';
import {
  MAX_VARIANT_HISTORY_NERV_DAYS,
  buildNervChartPoints,
  defaultNervRange,
} from '@/shared/variantHistoryNerv';
import { useVariantHistoryNerv } from '@/hooks/useVariantHistoryNerv';
import DailyNervSnapshotGraph from './DailyNervSnapshotGraph';
// Not used by the chart (stored values never wait on a sales import). The import is kept only so the
// type-check ratchet's per-file TS2589 baselines stay put: tsc's "excessively deep" reports land on
// whichever file reaches the Supabase query types first, and this edge keeps that file's position
// where it was before the chart stopped using the hook (UC-257 "As built"). Safe to drop together
// with the hook once the baselines are re-locked.
import '@/hooks/useFinancialSnapshotProcessing';

// Daily NERV Trends (FIG-238 · UC-257 · REQ-053): every point is that day's STORED ∑: NERV from
// Variant History (the DR% in effect that day), one total per merchant per day. There is no
// product or data-source filter, nothing is recalculated, and if the snapshots cannot be loaded
// the chart shows an error rather than falling back to a calculation. The chart ends at
// yesterday's snapshot (PT); the date controls show snapshot dates and today can't be selected.
// A day with no snapshot (Not recorded, or before recording began) has no point and the line breaks.

interface DailyNervViewProps {
  organisation: { id?: string } | null | undefined;
}

const DailyNervView: React.FC<DailyNervViewProps> = ({ organisation }) => {
  // PT calendar dates as 'YYYY-MM-DD' strings; the pickers convert at the edge with the local-date
  // helpers, so the browser's time zone (and DST) never shifts a day.
  const [range, setRange] = useState(() => defaultNervRange());
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  // Recomputed on render so a tab left open past midnight PT moves the latest date on.
  const latest = ptYesterday();
  const earliest = addDays(latest, -(MAX_VARIANT_HISTORY_NERV_DAYS - 1));

  const { status, snapshots, error, refetch } = useVariantHistoryNerv(organisation?.id, range.startDate, range.endDate);

  const points = useMemo(
    () => buildNervChartPoints(range.startDate, range.endDate, snapshots),
    [range.startDate, range.endDate, snapshots],
  );
  const plotted = points.filter((p) => p.nerv !== null).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily NERV Trends</CardTitle>
        <CardDescription>Track the merchant's ∑: NERV over time, as stored in Variant History</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex gap-2">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <Popover open={startPickerOpen} onOpenChange={setStartPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start" aria-label="Start Date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formatYmd(range.startDate)}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={ymdToLocalDate(range.startDate)}
                      defaultMonth={ymdToLocalDate(range.startDate)}
                      onSelect={(date) => {
                        if (date) {
                          setRange((prev) => ({ ...prev, startDate: localDateToYmd(date) }));
                          setStartPickerOpen(false);
                        }
                      }}
                      fromDate={ymdToLocalDate(earliest)}
                      toDate={ymdToLocalDate(range.endDate)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <Popover open={endPickerOpen} onOpenChange={setEndPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start" aria-label="End Date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formatYmd(range.endDate)}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={ymdToLocalDate(range.endDate)}
                      defaultMonth={ymdToLocalDate(range.endDate)}
                      onSelect={(date) => {
                        if (date) {
                          setRange((prev) => ({ ...prev, endDate: localDateToYmd(date) }));
                          setEndPickerOpen(false);
                        }
                      }}
                      fromDate={ymdToLocalDate(range.startDate)}
                      // Yesterday PT is the latest snapshot date: today can't be selected.
                      toDate={ymdToLocalDate(latest)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800" data-testid="daily-nerv-banner">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              {`NERV from Variant History: one snapshot per day at midnight PT, recorded at 6:00 AM PT. Showing data to ${formatYmd(latest)}.`}
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent>
              {status === 'error' ? (
                <div role="alert" data-testid="daily-nerv-error" className="border border-destructive/20 text-destructive rounded-md p-6 text-center flex flex-col items-center gap-3">
                  <span>{error} The chart is not recalculated, so nothing is shown until the snapshots load.</span>
                  <Button variant="outline" size="sm" onClick={refetch}>Try again</Button>
                </div>
              ) : status === 'loading' ? (
                <div className="border rounded-md p-6 text-center text-muted-foreground">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading NERV trends...
                  </div>
                </div>
              ) : plotted === 0 ? (
                <div className="border rounded-md p-6 text-center text-muted-foreground" data-testid="daily-nerv-empty">
                  No Variant History snapshots in the selected range. History starts on the day recording begins; earlier dates are not recalculated.
                </div>
              ) : (
                <div className="py-4">
                  <DailyNervSnapshotGraph points={points} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyNervView;
